class CollectionProductsGrid extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.isBusy = false;
    this.openButtons = Array.from(this.querySelectorAll('[data-open-modal]'));
    this.modal = this.querySelector('[data-modal]');
    this.modalPanel = this.querySelector('[data-modal-panel]');
    this.modalCloseButtons = Array.from(this.querySelectorAll('[data-close-modal]'));
    this.modalImage = this.querySelector('[data-modal-image]');
    this.modalTitle = this.querySelector('[data-modal-title]');
    this.modalPrice = this.querySelector('[data-modal-price]');
    this.modalComparePrice = this.querySelector('[data-modal-compare-price]');
    this.modalOptions = this.querySelector('[data-modal-options]');
    this.modalAddButton = this.querySelector('[data-modal-add]');
    this.modalError = this.querySelector('[data-modal-error]');
    this.modalStatus = this.querySelector('[data-modal-status]');
    this.routeCartAdd = this.dataset.routeCartAdd || '/cart/add.js';
    this.products = this.parseProducts();

    this.onOpenModalClick = this.handleOpenModalClick.bind(this);
    this.onCloseModalClick = this.closeModal.bind(this);
    this.onModalOptionClick = this.handleModalOptionClick.bind(this);
    this.onModalAddClick = this.handleModalAddClick.bind(this);
    this.onWindowKeydown = this.handleWindowKeydown.bind(this);

    this.openButtons.forEach((button) => button.addEventListener('click', this.onOpenModalClick));
    this.modalCloseButtons.forEach((button) => button.addEventListener('click', this.onCloseModalClick));
    this.modalOptions?.addEventListener('click', this.onModalOptionClick);
    this.modalAddButton?.addEventListener('click', this.onModalAddClick);
    window.addEventListener('keydown', this.onWindowKeydown);
  }

  disconnectedCallback() {
    this.openButtons?.forEach((button) => button.removeEventListener('click', this.onOpenModalClick));
    this.modalCloseButtons?.forEach((button) => button.removeEventListener('click', this.onCloseModalClick));
    this.modalOptions?.removeEventListener('click', this.onModalOptionClick);
    this.modalAddButton?.removeEventListener('click', this.onModalAddClick);
    window.removeEventListener('keydown', this.onWindowKeydown);
  }

  parseProducts() {
    const products = {};
    const scripts = this.querySelectorAll('[data-product-json]');

    scripts.forEach((script) => {
      const key = script.dataset.productKey;
      if (!key) return;

      try {
        products[key] = this.applyPaidVariantOverride(JSON.parse(script.textContent || '{}'));
      } catch (error) {
        console.error('Unable to parse product JSON for collection products grid.', error);
      }
    });

    return products;
  }

  getPaidVariantOverrideId(product) {
    const productId = String(product?.id || '');
    if (!productId) return '';

    return String(window.Theme?.variantOverrides?.paid?.[productId] || '');
  }

  applyPaidVariantOverride(product) {
    const overrideId = this.getPaidVariantOverrideId(product);
    if (!overrideId || !Array.isArray(product?.variants)) return product;

    const overrideVariant = product.variants.find((variant) => String(variant.id) === overrideId);
    if (!overrideVariant) return product;

    return {
      ...product,
      available: Boolean(overrideVariant.available),
      options: [],
      variants: [overrideVariant],
      selected_variant_id: overrideVariant.id,
    };
  }

  resolveAddableVariant(product, variant) {
    const overrideId = this.getPaidVariantOverrideId(product);
    if (!overrideId || !Array.isArray(product?.variants)) return variant;

    return product.variants.find((variantItem) => String(variantItem.id) === overrideId) || variant;
  }

  hasSelectableOptions(product) {
    if (!product?.options?.length) return false;
    if (product.options.length !== 1) return true;

    const option = product.options[0];
    return !(option.values.length === 1 && option.values[0] === 'Default Title');
  }

  getInitialSelections(product) {
    const selectedVariant =
      product.variants.find((variant) => variant.id === product.selected_variant_id && variant.available) ||
      product.variants.find((variant) => variant.available) ||
      product.variants[0];

    return Array.isArray(selectedVariant?.options) ? [...selectedVariant.options] : [];
  }

  getSelectedVariant() {
    if (!this.activeProduct) return null;

    return (
      this.activeProduct.variants.find((variant) =>
        variant.options.every((value, index) => value === this.activeSelections[index])
      ) || null
    );
  }

  isOptionValueAvailable(optionIndex, value) {
    if (!this.activeProduct) return false;

    return this.activeProduct.variants.some((variant) => {
      if (!variant.available) return false;

      return variant.options.every((variantValue, index) => {
        if (index === optionIndex) return variantValue === value;
        return variantValue === this.activeSelections[index];
      });
    });
  }

  handleOpenModalClick(event) {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement)) return;

    const key = button.dataset.productKey;
    const product = key ? this.products[key] : null;
    if (!product) return;

    if (!this.hasSelectableOptions(product)) {
      const variant =
        product.variants.find((variantItem) => variantItem.id === product.selected_variant_id) ||
        product.variants.find((variantItem) => variantItem.available) ||
        product.variants[0] ||
        null;

      if (!variant) return;

      this.activeTrigger = button;
      this.addVariantToCart(product, variant);
      return;
    }

    this.activeProduct = product;
    this.activeTrigger = button;
    this.activeSelections = this.getInitialSelections(product);
    this.renderModal();
    this.openModal();
  }

  renderModal() {
    const product = this.activeProduct;
    if (!product) return;

    const variant = this.getSelectedVariant() || product.variants.find((item) => item.available) || product.variants[0] || null;
    const image = variant?.image || product.featured_image || null;
    const addLabel = this.dataset.modalAddLabel || 'Add to cart';
    const soldOutLabel = this.dataset.soldOutLabel || 'Sold out';

    if (Array.isArray(variant?.options)) {
      this.activeSelections = [...variant.options];
    }

    if (this.modalTitle) {
      this.modalTitle.textContent = product.title || '';
    }

    if (this.modalImage instanceof HTMLImageElement) {
      this.modalImage.src = image?.src || '';
      this.modalImage.alt = image?.alt || product.title || '';
    }

    if (this.modalPrice) {
      this.modalPrice.textContent = variant?.price_text || '';
    }

    if (this.modalComparePrice) {
      const comparePrice = variant?.compare_price_text || '';
      this.modalComparePrice.textContent = comparePrice;
      this.modalComparePrice.hidden = !comparePrice;
    }

    if (this.modalOptions) {
      this.modalOptions.innerHTML = product.options
        .map((option, index) => {
          const selectedValue = this.activeSelections[index] || option.values[0] || '';
          const valuesMarkup = option.values
            .map((value) => {
              const isSelected = value === selectedValue;
              const isAvailable = this.isOptionValueAvailable(index, value);

              return `
                <button
                  class="collection-products-grid__modal-option-button${isSelected ? ' is-selected' : ''}${isAvailable ? '' : ' is-unavailable'}"
                  type="button"
                  data-option-index="${index}"
                  data-option-value="${this.escapeHtml(value)}"
                  ${isAvailable ? '' : 'aria-disabled="true"'}
                >
                  ${this.escapeHtml(value)}
                </button>
              `;
            })
            .join('');

          return `
            <div class="collection-products-grid__modal-option-group">
              <div class="collection-products-grid__modal-option-label">
                ${this.escapeHtml(option.name)}: <strong>${this.escapeHtml(selectedValue)}</strong>
              </div>
              <div class="collection-products-grid__modal-option-values">
                ${valuesMarkup}
              </div>
            </div>
          `;
        })
        .join('');
    }

    if (this.modalAddButton instanceof HTMLButtonElement) {
      const isAvailable = Boolean(variant?.available);
      this.modalAddButton.disabled = !isAvailable || this.isBusy;
      const label = isAvailable ? addLabel : soldOutLabel;
      const labelNode = this.modalAddButton.querySelector('span:last-child');
      if (labelNode) {
        labelNode.textContent = label;
      } else {
        this.modalAddButton.textContent = label;
      }
    }

    this.clearError();
  }

  handleModalOptionClick(event) {
    const button = event.target.closest('[data-option-index][data-option-value]');
    if (!(button instanceof HTMLButtonElement)) return;
    if (button.getAttribute('aria-disabled') === 'true') return;

    const optionIndex = Number.parseInt(button.dataset.optionIndex || '', 10);
    const optionValue = button.dataset.optionValue;

    if (Number.isNaN(optionIndex) || typeof optionValue !== 'string') return;

    this.activeSelections[optionIndex] = optionValue;
    this.renderModal();
  }

  async handleModalAddClick() {
    const product = this.activeProduct;
    const variant = this.getSelectedVariant();
    await this.addVariantToCart(product, variant);
  }

  async addVariantToCart(product, variant) {
    variant = this.resolveAddableVariant(product, variant);

    if (!product || !variant || !variant.available || this.isBusy) return;

    this.setBusy(true);
    this.clearError();

    const cartSections = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);

    const requestBody = {
      items: [
        {
          id: variant.id,
          quantity: 1,
        },
      ],
    };

    if (cartSections.length > 0) {
      requestBody.sections = cartSections.join(',');
      requestBody.sections_url = window.location.pathname;
    }

    try {
      const response = await fetch(this.routeCartAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        const message = data.description || data.message || 'Unable to add this item to the cart.';
        this.showError(message);
        return;
      }

      this.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: data,
            sourceId: this.id,
            data: {
              source: 'product-form-component',
              itemCount: 1,
              productId: product.id,
              sections: data.sections || {},
            },
          },
        })
      );

      if (this.modalStatus) {
        this.modalStatus.textContent = 'Added to cart';
      }

      if (this.modal && !this.modal.hidden) {
        this.closeModal();
      }

      this.openCartDrawer();
    } catch (error) {
      console.error(error);
      this.showError('Unable to add this item to the cart.');
    } finally {
      this.setBusy(false);
      if (this.modal && !this.modal.hidden) {
        this.renderModal();
      }
    }
  }

  setBusy(nextState) {
    this.isBusy = nextState;
    this.classList.toggle('is-busy', nextState);
    if (this.modalAddButton) {
      this.modalAddButton.disabled = nextState || this.modalAddButton.disabled;
    }
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (!(cartDrawer instanceof HTMLElement)) return;

    if (typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return;
    }

    if (typeof cartDrawer.showDialog === 'function') {
      cartDrawer.showDialog();
    }
  }

  openModal() {
    if (!(this.modal instanceof HTMLElement)) return;

    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.modal.hidden = false;
    this.modalPanel?.focus();
  }

  closeModal() {
    if (!(this.modal instanceof HTMLElement)) return;

    this.modal.hidden = true;
    document.body.style.overflow = this.previousBodyOverflow || '';
    this.activeTrigger?.focus();
    this.activeProduct = null;
  }

  handleWindowKeydown(event) {
    if (event.key === 'Escape' && this.modal && !this.modal.hidden) {
      this.closeModal();
    }
  }

  showError(message) {
    if (this.modalError instanceof HTMLElement) {
      this.modalError.hidden = false;
      this.modalError.textContent = message;
    }

    if (this.modalStatus) {
      this.modalStatus.textContent = message;
    }
  }

  clearError() {
    if (this.modalError instanceof HTMLElement) {
      this.modalError.hidden = true;
      this.modalError.textContent = '';
    }
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }
}

if (!customElements.get('collection-products-grid')) {
  customElements.define('collection-products-grid', CollectionProductsGrid);
}
